import qs from 'qs'
import {
  EmpDepartmentListResponse,
  CategoryListResponse,
  PaymentListResponse,
  CountriesListResponse,
  SubCategoryListResponse,
  ExportExpensesList,
  ExpensesListParams,
  ExpensesListResponse,
  ExpenseCurrencyListResponse,
  ExpensesDetailsListResponse,
  CreditCardListResponse,
  ProjectsListResponse,
  VendorListResponse,
  AuthorizedEmployee,
  ExpensesFormList,
} from '../../../../types/ExpenseManagement/ExpenseList/expensesListTypes'
import { ApiParams } from '../../../../types/commonTypes'
import {
  getAuthenticatedRequestConfig,
  useAxios,
} from '../../../../utils/apiUtils'
import { AllowedHttpMethods, expensesListApiConfig } from '../../apiList'

const getDepartmentsDetails = async (): Promise<
  EmpDepartmentListResponse[]
> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getEmpDepartments,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getCategoryDetails = async (): Promise<CategoryListResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getCategoryList,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getPaymentDetails = async (): Promise<PaymentListResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getPaymentList,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getCountryDetails = async (): Promise<CountriesListResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getCountries,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getSelectedSubCategories = async (
  categoryId: number[],
): Promise<SubCategoryListResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getSelectedSubCategories,
    method: AllowedHttpMethods.get,
    params: { categoryId },
    paramsSerializer: (params: ApiParams) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const exportExpensesListData = async (
  props: ExportExpensesList,
): Promise<Blob | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.exportExpenseList,
    method: AllowedHttpMethods.get,
    params: {
      categoryId: props.categoryId ?? '',
      country: props.country ?? '',
      dateSelection: props.dateSelection ?? '',
      departmentId: props.departmentId,
      from: props.from ?? '',
      multipleSearch: props.multipleSearch ?? '',
      paymentMode: props.paymentMode ?? '',
      subCategoryId: props.subCategoryId,
      to: props.to ?? '',
      token: localStorage.getItem('token') ?? '',
    },
    responseType: 'blob',
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getExpensesList = async (
  props: ExpensesListParams,
): Promise<ExpensesListResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getExpensesList,
    method: AllowedHttpMethods.get,
    params: {
      categoryId: props.categoryId,
      country: props.country,
      dateSelection: props.dateSelection,
      departmentId: props.departmentId,
      endIndex: props.endIndex,
      from: props.from,
      multipleSearch: props.multipleSearch,
      paymentMode: props.paymentMode,
      startIndex: props.startIndex,
      subCategoryId: props.subCategoryId,
      to: props.to,
    },
    paramsSerializer: (params: ApiParams) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const updateExpenseDetails = async (
  data: ExpensesFormList,
): Promise<ExpensesFormList> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.updateExpensesList,
    method: AllowedHttpMethods.put,
    data,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const deletedExpenseRecord = async (
  formId: number,
): Promise<number | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.deleteExpense,
    method: AllowedHttpMethods.delete,
    params: { formId },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const addCopiedExpensesList = async (
  formIds: number[],
): Promise<number | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.addCopiedExpensesList,
    method: AllowedHttpMethods.post,
    params: { formIds },
    paramsSerializer: (params: ApiParams) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getExpensesCurrencyList = async (): Promise<
  ExpenseCurrencyListResponse[]
> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getCurrencyList,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getExpenseDetailsList = async (
  formId: string,
): Promise<ExpensesFormList> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getExpenseDetails,
    method: AllowedHttpMethods.get,
    params: { formId },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const editCategories = async (
  categoryId: number,
): Promise<CategoryListResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.editCategory,
    method: AllowedHttpMethods.get,
    params: { categoryId },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getSubCategories = async (
  categoryId: number,
): Promise<SubCategoryListResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getSubCategories,
    method: AllowedHttpMethods.get,
    params: { categoryId },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const updateExpenses = async (
  data: ExpensesDetailsListResponse,
): Promise<ExpensesDetailsListResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.updateExpenses,
    method: AllowedHttpMethods.put,
    data,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getCreditCardsList = async (): Promise<CreditCardListResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getCardsList,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const vendorsList = async (
  searchStrings: string,
): Promise<VendorListResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getVendorList,
    method: AllowedHttpMethods.get,
    params: {
      searchStr: searchStrings,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const projectsList = async (
  searchString: string,
): Promise<ProjectsListResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getMatchedProjects,
    method: AllowedHttpMethods.get,
    params: {
      searchStr: searchString,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getAllEmployeesList = async (
  searchString: string,
): Promise<AuthorizedEmployee[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: expensesListApiConfig.getEmployeeList,
    method: AllowedHttpMethods.get,
    params: {
      searchStr: searchString,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}
const expensesListApi = {
  getDepartmentsDetails,
  getCategoryDetails,
  getPaymentDetails,
  getCountryDetails,
  getExpensesList,
  getSelectedSubCategories,
  exportExpensesListData,
  updateExpenseDetails,
  deletedExpenseRecord,
  addCopiedExpensesList,
  getExpensesCurrencyList,
  getExpenseDetailsList,
  editCategories,
  getSubCategories,
  updateExpenses,
  getCreditCardsList,
  vendorsList,
  projectsList,
  getAllEmployeesList,
}

export default expensesListApi
